import { Sun, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useFilters } from "../context/FilterContext";
import { useFetch } from "../lib/useFetch";
import api from "../lib/api";
import { KpiCard } from "../components/common/KpiCard";
import { InsightCard } from "../components/common/InsightCard";
import { Panel, Loading } from "../components/common/ui";
import { cn } from "../lib/utils";

const toneMap = {
  good: "text-emerald-600 bg-emerald-50 border-emerald-200",
  attention: "text-red-600 bg-red-50 border-red-200",
  risk: "text-amber-600 bg-amber-50 border-amber-200",
  opportunity: "text-emerald-700 bg-secondary/10 border-secondary/30",
  ai: "text-primary bg-accent border-primary/20",
};

export default function Dashboard() {
  const { range, hub } = useFilters();
  const q = `?range=${range}&hub=${hub}`;
  const { data: kpiData, loading: kLoad } = useFetch(`/dashboard/kpis${q}`, [range, hub]);
  const { data: insights, loading: iLoad, } = useFetch("/dashboard/insights", []);
  const [refreshKey, setRefreshKey] = useState(0);
  const { data: top10 } = useFetch("/dashboard/top10", [refreshKey]);
  const { data: brief, loading: bLoad } = useFetch("/dashboard/briefing", []);
  const [refetchInsights, setRefetchInsights] = useState(0);
  const { data: feed } = useFetch("/dashboard/insights", [refetchInsights]);

  const reloadBrief = async () => {
    await api.post("/dashboard/briefing/refresh");
    setRefreshKey((k) => k + 1);
    window.location.reload();
  };

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Daily briefing */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="bg-primary text-white px-6 py-5 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/15 grid place-items-center"><Sun className="w-5 h-5" /></div>
              <div>
                <div className="font-heading text-xl font-bold">{(brief?.greeting || "Good Morning").replace(/[,\s]+$/, "")}</div>
                <div className="text-white/80 text-sm mt-0.5 max-w-3xl">{bLoad ? "Preparing your briefing…" : brief?.summary}</div>
              </div>
            </div>
            <button onClick={reloadBrief} className="text-white/70 hover:text-white text-xs flex items-center gap-1" data-testid="briefing-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 p-5">
          {(brief?.sections || []).map((s, i) => (
            <div key={i} className={cn("rounded-lg border p-3", toneMap[s.tone] || "bg-slate-50 border-slate-200")}>
              <div className="text-[11px] font-bold uppercase tracking-wide">{s.label}</div>
              <p className="text-xs mt-1 text-slate-700 leading-snug">{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div>
        <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3">Business Health</h2>
        {kLoad ? <Loading rows={2} /> : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-3">
            {(kpiData?.kpis || []).map((k) => <KpiCard key={k.key} k={k} />)}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="What needs my attention?" subtitle="AI-generated business insights" testid="insight-feed">
          <div className="space-y-3">
            {!feed ? <Loading /> : feed.slice(0, 6).map((it) => (
              <InsightCard key={it.id} item={it} onChange={() => setRefetchInsights((k) => k + 1)} />
            ))}
          </div>
        </Panel>

        <Panel title="Top 10 Things I Should Know Today" subtitle="Prioritised by business impact" testid="top10">
          <div className="space-y-3">
            {!top10 ? <Loading /> : top10.map((it, i) => (
              <InsightCard key={it.id} item={it} rank={i + 1} onChange={() => setRefreshKey((k) => k + 1)} />
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
